import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Helpers from "../../utils/helpers";
import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { Button } from "primereact/button";
import { getActiveOrderProducts, getOrderReviewTotals, toRoundedWholeNumber } from "./order-review-calculations";
import orderServices from "../../services/orders/orderservice";
import CourierProviderService from "../../services/courier-provider/courier-provider.service";

interface selectedOrderDataProps {
  orderinfo: any;
}

const OrderPrint: React.FC<selectedOrderDataProps> = ({ orderinfo }) => {
  const orderRef = useRef(null);
  const [instructions, setInstructions] = useState<{ customer: string; order: string }>({ customer: "", order: "" });
  const [courierProviderName, setCourierProviderName] = useState("");

  useEffect(() => {
    let active = true;
    const directName = orderinfo?.courierProviderName || orderinfo?.courierProvider || orderinfo?.providerName || "";
    setCourierProviderName(directName);
    const providerId = Number(orderinfo?.courierProviderID ?? orderinfo?.courierProviderId);
    if (directName || !Number.isSafeInteger(providerId) || providerId <= 0) return () => { active = false; };

    CourierProviderService.getAllCourierProviders().then((providers: any[]) => {
      if (!active || !Array.isArray(providers)) return;
      const selectedProvider = providers.find(provider =>
        Number(provider.courierProviderID ?? provider.courierProviderId ?? provider.id) === providerId
      );
      setCourierProviderName(selectedProvider?.providerName || selectedProvider?.courierProviderName || "");
    }).catch(() => {
      if (active) setCourierProviderName("");
    });

    return () => { active = false; };
  }, [orderinfo?.courierProviderID, orderinfo?.courierProviderId, orderinfo?.courierProviderName, orderinfo?.courierProvider, orderinfo?.providerName]);

  useEffect(() => {
    let active = true;
    const loadInstructions = async () => {
      const orderId = Number(orderinfo?.orderID);
      if (!Number.isSafeInteger(orderId) || orderId <= 0) {
        setInstructions({ customer: "", order: "" });
        return;
      }
      const [orderResponse, customerResponse] = await Promise.all([
        orderServices.getOrderInstructions(orderId),
        (orderinfo?.userID ?? orderinfo?.customerID) ? orderServices.getCustomerInstruction(Number(orderinfo.userID ?? orderinfo.customerID)) : Promise.resolve(null),
      ]);
      if (!active) return;
      const customerFromOrder = orderResponse?.instructions
        ?.filter((item: any) => item.instructionType === "CI")
        .map((item: any) => item.instruction)
        .filter(Boolean)
        .join("\n");
      const orderInstruction = orderResponse?.orderComments || orderResponse?.orderInstruction || orderResponse?.instructions
        ?.filter((item: any) => item.instructionType === "OSI" || item.instructionType === "OI")
        .map((item: any) => item.instruction)
        .filter(Boolean)
        .join("\n") || orderResponse?.instruction || "";
      const customerInstruction = customerResponse?.instruction ?? customerResponse?.customerInstruction ?? customerFromOrder ?? "";
      setInstructions({ customer: String(customerInstruction || "").trim(), order: String(orderInstruction || "").trim() });
    };
    void loadInstructions();
    return () => { active = false; };
  }, [orderinfo?.orderID, orderinfo?.userID]);

  const handleOrderPrint = useReactToPrint({
    content: () => orderRef.current,
    documentTitle: 'Order Details',
    pageStyle: `@page { margin-top: 5mm; margin-left: 5mm; margin-right: 3mm; margin-bottom: 1mm; }`
  });

  if (!orderinfo) {
    return null;
  }

  const totals = getOrderReviewTotals(orderinfo, { includeNegativeAmounts: true });
  const products = getActiveOrderProducts(orderinfo.products || [], { includeNegativeAmounts: true });


  const formatAddressLine = (parts: (string | undefined)[], separator = ', ') => {
    const validParts = parts.filter(part => part && part.trim() !== '');
    return validParts.join(separator);
  };

  const renderDeliveryAddress = () => {
    const addressParts = [
      orderinfo.address1,
      orderinfo.address2,
      orderinfo.landmark,
      orderinfo.areaName,
      orderinfo.subDistrictName,
      orderinfo.cityName,
      orderinfo.stateName,
      orderinfo.countryName
    ].filter(Boolean);

    return (
      <div className="col">
        <span className="text-primary">
          {formatAddressLine(addressParts)}
        </span><br />
        {orderinfo.zipcode && `Pin: ${orderinfo.zipcode}`}
      </div>
    );
  };

  const productImageTemplate = (product: any) => {
    const defaultImage = process.env.PUBLIC_URL + "/assests/images/Desiri-Groundnut-Oils.png";
    const resolvedUrl = product.productImageURL || product.imageUrl || product.url || defaultImage;

    return (
      <img 
        src={resolvedUrl} 
        alt={product.productName} 
        onError={(e: any) => {
          e.target.src = defaultImage;
        }}
        style={{ objectFit: 'contain', borderRadius: '4px', width: '50px', height: '50px', display: 'block' }}
      />
    );
  };

  const priceBodyTemplate = (product: any) => {
    return Helpers.formatAmountInINR(product.unitPrice);
  };

  const quantityBodyTemplate = (product: any) => {
    return `${product.quantity} ${product.measurementTypeName || product.measurementTypeCode || ''}`;
  };

  const amountBodyTemplate = (product: any) => {
    return Helpers.formatAmountInINR(product.amount || (product.unitPrice * product.quantity));
  };

  const taxBodyTemplate = (product: any) => {
    return Helpers.formatAmountInINR(product.tax);
  };

  const totalAmountBodyTemplate = (product: any) => {
    return Helpers.formatAmountInINR(Math.round(
      (product.amount || (product.unitPrice * product.quantity)) + (product.tax || 0))
    );
  };

  const expectedDeliveryTemplate = (product: any) => {
    return product.expectedDeliveryDate ? 
      new Date(product.expectedDeliveryDate).toLocaleDateString("en-gb", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      }) : 
      'N/A';
  };

  const headerTemplate = (
    <>
      <div className="grid">
        <div className="col-6">
          <div className="grid mb-3">
            <img src={`${process.env.PUBLIC_URL}/assests/images/DesiriLogo.png`} width={100} alt="Desiri Logo" />
          </div>
          {orderinfo.invoices?.[0]?.poS_Address1 && (
            <div>
              <div className="grid">
                <div className="col">From:<br /> <span className="text-primary">Desiri Naturals Pvt Ltd</span></div>
              </div>
              <div className="grid">
                <div className="col">
                  <span className="text-primary">
                    {[
                      orderinfo.invoices[0]?.poS_Address1,
                      orderinfo.invoices[0]?.poS_Address2,
                      orderinfo.invoices[0]?.poS_CityName,
                      orderinfo.invoices[0]?.poS_LandMark,
                      orderinfo.invoices[0]?.poS_StateName,
                      orderinfo.invoices[0]?.poS_CountryName,
                    ].filter(Boolean).join(', ')}
                  </span><br />
                  {`Pin: ${orderinfo.invoices[0]?.poS_Zipcode}`}
                </div>
              </div>
              <div className="grid">
                <div className="col">
                  GST #: <span className="text-primary">29AAGCD5864J1Z4</span>
                </div>
              </div>
            </div>
            )}
          <div className="grid">
            <div className="col">To:<br /> <span className="text-primary">{orderinfo.userName}</span></div>
          </div>
          <div className="grid">
            <div className="col">Delivery Address:<br />
              {renderDeliveryAddress()}
            </div>
          </div>
          <div className="grid">
            <div className="col">Mobile #: <span className="text-primary">{orderinfo.primaryContactNbr}</span></div>
          </div>
          {orderinfo.gsT_Number && (
            <div className="grid">
              <div className="col">GST #: <span className="text-primary">{orderinfo.gsT_Number}</span></div>
            </div>
          )}
        </div>
        <div className="col-1"></div>
        <div className="col-5">
          <div className="grid mt-7">
            <div className="col">Order #: <span className="text-primary">{orderinfo.orderID}</span></div>
          </div>
          <div className="grid">
            <div className="col">Order Date: <span className="text-primary">
              {new Date(orderinfo.orderDate).toLocaleDateString("en-gb", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              })}
            </span></div>
          </div>
          <div className="grid">
            <div className="col">Order Amount: <span className="text-primary">
              {Helpers.formatAmountInINR(toRoundedWholeNumber(totals.totalBeforeReward))}
            </span></div>
          </div>
          <div className="grid">
            <div className="col">Payment Mode: <span className="text-primary">{Helpers.formatPaymentMode(orderinfo.paymentMode)}</span></div>
          </div>
          {courierProviderName && <div className="grid">
            <div className="col">Courier Provider: <span className="text-primary">{courierProviderName}</span></div>
          </div>}
          {instructions.customer && <div className="grid">
            <div className="col">Customer Instruction: <span className="text-primary">{instructions.customer}</span></div>
          </div>}
          {instructions.order && <div className="grid">
            <div className="col">Order Instruction: <span className="text-primary">{instructions.order}</span></div>
          </div>}
        </div>
      </div>
    </>
  );

  const footerTemplate = (
    <>
      <div className="grid">
        <div className="col-7"></div>
        <div className="col-5">
          <div className="grid">
            <div className="col">Taxable Amount:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(totals.taxableAmount)}</div>
            </div>
          </div>
          <div className="grid">
            <div className="col">Tax Amount:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(totals.taxAmount)}</div>
            </div>
          </div>
          {
            totals.courierCharges > 0 ? 
            <div className="grid">
            <div className="col">Courier Charges:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(totals.courierCharges)}</div>
            </div>
          </div> : ""
          }
          { totals.paymentGatewayCharges > 0 ?
            <div className="grid">
            <div className="col">Payment Gateway Charges:
              <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(totals.paymentGatewayCharges)}</div>
            </div>
          </div> : ""
          }
          {totals.discountAmount > 0 && (
            <div className="grid">
              <div className="col">Discount Amount:
                <div className="mr-2" style={{ float: "right" }}>{Helpers.formatAmountInINR(totals.discountAmount)}</div>
              </div>
            </div>
          )}
          <div className="grid">
            <div className="col">Total Amount:
              <div className="mr-2 text-primary" style={{ float: "right" }}>
                {Helpers.formatAmountInINR(toRoundedWholeNumber(totals.totalBeforeReward))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Button 
        icon="pi pi-print" 
        style={{ float: "right", marginRight: 2 }} 
        onClick={handleOrderPrint} 
        rounded 
        text 
        severity="info" 
        aria-label="Print" 
      />
      <div ref={orderRef} style={{ overflowX: 'hidden' }}>
        <DataTable
          value={products}
          className="no-flex"
          header={headerTemplate}
          footer={footerTemplate}
          size="small"
        >
          <Column header="Image" body={productImageTemplate} style={{ width: '80px' }} />
          <Column field="productName" header="Product Name" />
          <Column header="Unit Price" body={priceBodyTemplate} />
          <Column header="Quantity" body={quantityBodyTemplate} />
          <Column header="Amount" body={amountBodyTemplate} />
          <Column header="Tax" body={taxBodyTemplate} />
          <Column header="Total" body={totalAmountBodyTemplate} />
          <Column header="Expected Delivery" body={expectedDeliveryTemplate} />
        </DataTable>
      </div>
    </>
  );
};

export default OrderPrint;
